import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { Box, Button, Dialog, DialogContent, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

// project-imports
import IconButton from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import {
  INTERNATIONAL_DISCOUNT_PERCENTAGE,
  INTERNATIONAL_FINAL_PRICE,
  INTERNATIONAL_REFERENCE_PRICE,
  parseInternationalOffer
} from 'utils/internationalOffer';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// assets
import { Add, DiscountShape } from 'iconsax-react';

const MIN_DELAY_MS = 5 * 60 * 1000;
const MAX_DELAY_MS = 10 * 60 * 1000;

const randomDelay = () => MIN_DELAY_MS + Math.floor(Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS + 1));

const formatPrice = (value: number) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: value % 1 ? 2 : 0,
    maximumFractionDigits: value % 1 ? 2 : 0
  }).format(value);

const formatShortCountdownLabel = (label: string) => (label.length > 4 ? `${label.slice(0, 4)}.` : label);

function CountdownLabel({ label, compactWhenNeeded }: { label: string; compactWhenNeeded: boolean }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const [shouldCompact, setShouldCompact] = useState(false);

  useEffect(() => {
    if (!compactWhenNeeded) {
      setShouldCompact(false);
      return undefined;
    }

    const measure = () => {
      if (!containerRef.current || !measureRef.current) return;
      setShouldCompact(measureRef.current.scrollWidth > containerRef.current.clientWidth);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [compactWhenNeeded, label]);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', minWidth: 0, textAlign: 'center' }}>
      <Typography variant="caption" fontWeight={700} color="common.white" textTransform="uppercase" noWrap>
        {shouldCompact ? formatShortCountdownLabel(label) : label}
      </Typography>
      <Typography
        ref={measureRef}
        component="span"
        variant="caption"
        fontWeight={700}
        color="common.white"
        textTransform="uppercase"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none'
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const InternationalOfferDialog = () => {
  const theme = useTheme();
  const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const timerRef = useRef<number | null>(null);

  const offer = useMemo(() => parseInternationalOffer(user?.parentOffer || user?.offer), [user?.offer, user?.parentOffer]);
  const isPro = String(user?.currentPlan || '').includes('pro');
  const canShow = String(user?.code || user?.parentCode || '').toUpperCase() === 'INTERNATIONAL' && !!offer?.isActive && !isPro;

  const countdownParts = useMemo(() => {
    const totalSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [
      { value: days, labelId: 'account.subscription.planSelection.internationalOfferDialog.countdown.days' },
      { value: hours, labelId: 'account.subscription.planSelection.internationalOfferDialog.countdown.hours' },
      { value: minutes, labelId: 'account.subscription.planSelection.internationalOfferDialog.countdown.minutes' },
      { value: seconds, labelId: 'account.subscription.planSelection.internationalOfferDialog.countdown.seconds' }
    ];
  }, [remainingMs]);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleNext = () => {
    clearTimer();
    if (!canShow) return;
    timerRef.current = window.setTimeout(() => setOpen(true), randomDelay());
  };

  useEffect(() => {
    clearTimer();
    if (canShow) {
      setOpen(true);
      scheduleNext();
    } else {
      setOpen(false);
    }

    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShow]);

  useEffect(() => {
    if (!canShow || !offer?.expiresAt) return undefined;

    const updateRemaining = () => setRemainingMs(Math.max(offer.expiresAt - Date.now(), 0));
    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);

    return () => window.clearInterval(interval);
  }, [canShow, offer?.expiresAt]);

  const handleClose = () => {
    setOpen(false);
    scheduleNext();
  };

  const handleCta = () => {
    setOpen(false);
    scheduleNext();
    navigate('/account/subscription');
  };

  if (!canShow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: theme.palette.background.default }}>
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <DiscountShape variant="Bulk" color={theme.palette.primary.main} />
                <Typography variant="h4">
                  <FormattedMessage id="account.subscription.planSelection.internationalOfferDialog.title" />
                </Typography>
              </Stack>
              <IconButton size="small" color="error" onClick={handleClose}>
                <Add size={18} style={{ transform: 'rotate(45deg)' }} />
              </IconButton>
            </Stack>

            <Typography variant="body1" color="text.secondary">
              <FormattedMessage id="account.subscription.planSelection.internationalOfferDialog.description" />
            </Typography>

            <Stack spacing={1.25}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '8px 8px 0 0',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.common.white
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={1}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={900} sx={{ opacity: 0.95, lineHeight: 1.35 }}>
                      <FormattedMessage
                        id="account.subscription.planSelection.internationalOffer.discount"
                        values={{ discount: INTERNATIONAL_DISCOUNT_PERCENTAGE }}
                      />
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      <FormattedMessage id="account.subscription.planSelection.internationalOfferDialog.includes" />
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="h5" sx={{ textDecoration: 'line-through', opacity: 0.75 }}>
                      {formatPrice(INTERNATIONAL_REFERENCE_PRICE)}€
                    </Typography>
                    <Typography variant="h3">{formatPrice(INTERNATIONAL_FINAL_PRICE)}€</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                      / {intl.formatMessage({ id: 'common.others.year' })}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction="row" spacing={1.25} justifyContent="space-between" sx={{ width: '100%' }}>
                {countdownParts.map((part, index) => (
                  <Box
                    key={part.labelId}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      width: '100%',
                      minHeight: { xs: 76, sm: 86 },
                      py: 1.25,
                      px: 1,
                      borderRadius: index === 0 ? '0 0 0 8px' : index === countdownParts.length - 1 ? '0 0 8px 0' : 0,
                      bgcolor: theme.palette.info.main,
                      color: theme.palette.common.white,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      boxShadow: theme.customShadows?.z1
                    }}
                  >
                    <Typography variant="h3" color={theme.palette.common.white}>
                      {String(part.value).padStart(2, '0')}
                    </Typography>
                    <CountdownLabel label={intl.formatMessage({ id: part.labelId })} compactWhenNeeded={matchDownSm} />
                  </Box>
                ))}
              </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
              <Button variant="outlined" color="secondary" onClick={handleClose}>
                <FormattedMessage id="common.others.close" />
              </Button>
              <Button variant="contained" onClick={handleCta}>
                <FormattedMessage id="account.subscription.planSelection.internationalOfferDialog.cta" />
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InternationalOfferDialog;
